import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Download, Sparkles, Feather, RotateCcw, Copy } from "lucide-react";
import { STYLE_CARDS } from "@/data/styles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "lcs_session_id";

const ORDINALS = ["", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];
function ordinal(n) {
  if (n <= 10) return ORDINALS[n];
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function queueCopy(position, etaSeconds) {
  const mins = Math.max(1, Math.round((etaSeconds || 45) / 60));
  const time = mins === 1 ? "about a minute away" : `about ${mins} minutes away`;
  if (!position || position <= 1) return `You're next in line. Your character is ${time}.`;
  return `You're ${ordinal(position)} in line. Your character is ${time}.`;
}

const fade = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -15 },
  transition: { duration: 0.6, ease: "easeOut" },
};

function TypingBubble({ label }) {
  return (
    <div className="flex items-center gap-3 py-3" data-testid="guide-typing">
      <div className="flex items-center gap-1.5">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
      {label && <span className="text-sm font-sans text-[#4A6C6D] italic">{label}</span>}
    </div>
  );
}

function GuideMessage({ msg }) {
  return (
    <motion.div {...fade} className="mb-8" data-testid="guide-message">
      <p className="font-serif text-2xl md:text-3xl leading-snug text-[#0B2D30] whitespace-pre-wrap">
        {msg.content}
      </p>
      {msg.image && (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          className="mt-7 rounded-2xl overflow-hidden border border-[#E8D8B0] shadow-lg max-w-md"
        >
          <img
            src={`data:image/png;base64,${msg.image}`}
            alt="Your character"
            className="w-full h-auto block"
            data-testid="character-image"
          />
        </motion.div>
      )}
      {msg.question && (
        <p className="mt-6 font-serif text-xl md:text-2xl leading-relaxed text-[#0B2D30] whitespace-pre-wrap">
          {msg.question}
        </p>
      )}
    </motion.div>
  );
}

function ClientMessage({ msg }) {
  return (
    <motion.div {...fade} className="mb-8 flex justify-end" data-testid="client-message">
      <p className="font-sans text-base md:text-lg text-[#4A6C6D] bg-[#F1EBE1] rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] leading-relaxed">
        {msg.content}
      </p>
    </motion.div>
  );
}

function SummaryReview({ summary, onConfirm, onAdjust, busy }) {
  const [adjusting, setAdjusting] = useState(false);
  const [text, setText] = useState("");
  return (
    <motion.div {...fade} className="mb-8" data-testid="summary-review">
      <div className="bg-[#F1EBE1] border border-[#E8D8B0] rounded-2xl p-6 md:p-8 whitespace-pre-wrap font-sans text-base md:text-lg leading-relaxed text-[#0B2D30]">
        {summary}
      </div>
      {!adjusting ? (
        <div className="mt-7 flex flex-col sm:flex-row gap-4">
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-summary-button"
            className="rounded-full bg-[#114A4E] text-[#FAF8F5] px-8 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans disabled:opacity-50 flex items-center justify-center gap-2"
          >
            Yes, this is my story <Check size={18} />
          </button>
          <button
            onClick={() => setAdjusting(true)}
            disabled={busy}
            data-testid="adjust-summary-button"
            className="rounded-full bg-transparent border-2 border-[#C5A059] text-[#0B2D30] px-8 py-4 hover:bg-[#C5A059] hover:text-[#FAF8F5] transition-colors duration-300 text-lg font-medium font-sans disabled:opacity-50"
          >
            I'd like to adjust something
          </button>
        </div>
      ) : (
        <div className="mt-7">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Tell me what to adjust..."
            data-testid="adjust-summary-input"
            rows={3}
            className="w-full bg-transparent border-0 border-b-2 border-[#E8D8B0] focus:border-[#C5A059] text-[#0B2D30] text-xl font-serif resize-none py-3 placeholder-[#4A6C6D]/40 transition-colors"
          />
          <button
            onClick={() => text.trim() && onAdjust(text.trim())}
            disabled={busy || !text.trim()}
            data-testid="send-adjust-button"
            className="mt-4 rounded-full bg-[#114A4E] text-[#FAF8F5] px-8 py-3 hover:bg-[#0A3336] transition-colors text-base font-medium font-sans disabled:opacity-40 flex items-center gap-2"
          >
            Send <ArrowRight size={16} />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function StyleSelect({ styles, link, onSelect, busy }) {
  const [selected, setSelected] = useState(null);
  const imgByName = Object.fromEntries(STYLE_CARDS.map((c) => [c.name, c.image]));
  return (
    <motion.div {...fade} className="mb-8" data-testid="style-select">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {styles.map((s) => {
          const active = selected === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSelected(s.id)}
              data-testid={`style-card-${s.id.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-left rounded-2xl border bg-[#FAF8F5] overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group ${
                active ? "border-[#C5A059] ring-2 ring-[#C5A059] shadow-lg" : "border-[#E8D8B0]"
              }`}
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={s.image || imgByName[s.name]}
                  alt={s.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {active && (
                  <span className="absolute top-3 right-3 bg-[#C5A059] text-[#FAF8F5] rounded-full p-1.5">
                    <Check size={16} />
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-serif text-xl text-[#0B2D30]">{s.name}</h3>
                <p className="font-sans text-sm text-[#4A6C6D] mt-1 leading-snug">{s.description}</p>
              </div>
            </button>
          );
        })}
      </div>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="style-examples-link"
        className="inline-block mt-6 font-sans text-base text-[#114A4E] underline underline-offset-4 hover:text-[#C5A059] transition-colors"
      >
        See style examples →
      </a>
      <div className="mt-6">
        <button
          onClick={() => selected && onSelect(selected)}
          disabled={busy || !selected}
          data-testid="confirm-style-button"
          className="rounded-full bg-[#114A4E] text-[#FAF8F5] px-10 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans disabled:opacity-40 flex items-center gap-2"
        >
          Continue <ArrowRight size={18} />
        </button>
      </div>
    </motion.div>
  );
}

export default function StoryDrawer() {
  const [started, setStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputType, setInputType] = useState("text");
  const [summary, setSummary] = useState("");
  const [styles, setStyles] = useState([]);
  const [styleLink, setStyleLink] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [phase, setPhase] = useState(1);
  const [docId, setDocId] = useState(null);
  const [characterImage, setCharacterImage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [resuming, setResuming] = useState(true);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, inputType, loading, queueStatus]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const pushGuide = (resp) => {
    setMessages((m) => [
      ...m,
      { id: Date.now() + Math.random(), role: "guide", content: resp.message, image: resp.image_base64, question: resp.question },
    ]);
  };

  const pollGeneration = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/generation/${sid}`);
      if (data.status === "queued" || data.status === "processing") {
        setQueueStatus({ position: data.queue_position, eta: data.eta_seconds });
        setInputType("generating");
        pollRef.current = setTimeout(() => pollGeneration(sid), 4000);
      } else if (data.status === "done") {
        setQueueStatus(null);
        setCharacterImage(data.image_base64);
        setMessages((m) => [
          ...m,
          { id: Date.now() + Math.random(), role: "guide", content: "", image: data.image_base64, question: data.proof },
        ]);
        setInputType("email");
      } else if (data.status === "failed") {
        setQueueStatus(null);
        setMessages((m) => [
          ...m,
          { id: Date.now() + Math.random(), role: "guide", content: data.message },
        ]);
        setInputType("generation_failed");
      } else {
        setQueueStatus(null);
        setInputType("summary");
      }
    } catch (e) {
      pollRef.current = setTimeout(() => pollGeneration(sid), 5000);
    }
  };

  const applyResponse = (resp) => {
    setPhase(resp.phase || phase);
    if (resp.input_type === "style") {
      setStyles(resp.styles || []);
      setStyleLink(resp.style_link || "");
    }
    if (resp.input_type === "summary") setSummary(resp.summary || "");
    if (resp.image_base64) setCharacterImage(resp.image_base64);
    if (resp.doc_id) setDocId(resp.doc_id);

    if (resp.input_type === "generating") {
      pushGuide({ message: resp.message });
      setQueueStatus({ position: resp.queue_position, eta: resp.eta_seconds });
      setInputType("generating");
      stopPolling();
      pollRef.current = setTimeout(() => pollGeneration(resp.session_id), 3000);
      return;
    }
    if (resp.input_type === "generation_busy") {
      pushGuide({ message: resp.message });
      setInputType("generation_failed");
      return;
    }
    if (resp.input_type === "done") localStorage.removeItem(SESSION_KEY);
    setInputType(resp.input_type);
    pushGuide(resp);
  };

  const begin = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/session/start`);
      setSessionId(data.session_id);
      localStorage.setItem(SESSION_KEY, data.session_id);
      setStarted(true);
      setPhase(data.phase);
      setInputType(data.input_type);
      pushGuide({ message: data.message });
    } finally {
      setLoading(false);
    }
  };

  const resumeSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/session/${sid}`);
      if (data.stage === "complete") {
        localStorage.removeItem(SESSION_KEY);
        return;
      }
      const rebuilt = (data.history || []).map((h, i) => ({
        id: `h-${i}`, role: h.role, content: h.content,
      }));
      if (data.image_base64) {
        rebuilt.push({ id: "resume-img", role: "guide", content: "", image: data.image_base64 });
      }
      setMessages(rebuilt);
      setSessionId(sid);
      setStarted(true);
      setPhase(data.phase || 1);
      setSummary(data.summary || "");
      if (data.image_base64) setCharacterImage(data.image_base64);
      if (data.doc_id) setDocId(data.doc_id);
      if (data.styles) { setStyles(data.styles); setStyleLink(data.style_link || ""); }
      if (data.stage === "generating") {
        setInputType("generating");
        pollRef.current = setTimeout(() => pollGeneration(sid), 500);
      } else {
        setInputType(data.input_type || "text");
      }
    } catch (e) {
      localStorage.removeItem(SESSION_KEY);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(SESSION_KEY);
    if (saved) {
      resumeSession(saved).finally(() => setResuming(false));
    } else {
      setResuming(false);
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callChat = async (intent, value, label) => {
    setLoading(true);
    setLoadingLabel(label || "");
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, text: value || "", intent });
      applyResponse(data);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { id: Date.now(), role: "guide", content: "I'm sorry, something interrupted us. Could you share that once more?" },
      ]);
    } finally {
      setLoading(false);
      setLoadingLabel("");
    }
  };

  const sendText = async () => {
    const value = text.trim();
    if (!value) return;
    setMessages((m) => [...m, { id: Date.now(), role: "client", content: value }]);
    setText("");
    const intentMap = { text: "reply", email: "submit_email" };
    await callChat(intentMap[inputType] || "reply", value);
  };

  const selectStyle = async (styleId) => {
    setMessages((m) => [...m, { id: Date.now(), role: "client", content: `I'd like the ${styleId} style.` }]);
    await callChat("select_style", styleId);
  };

  const confirmSummary = async () => {
    setMessages((m) => [...m, { id: Date.now(), role: "client", content: "Yes, this feels true to my story." }]);
    await callChat("confirm_summary", "", "Preparing your character...");
  };

  const retryGeneration = async () => {
    await callChat("confirm_summary", "", "Preparing your character...");
  };

  const adjustSummary = async (value) => {
    setMessages((m) => [...m, { id: Date.now(), role: "client", content: value }]);
    await callChat("adjust_summary", value);
  };

  const startOver = () => {
    stopPolling();
    localStorage.removeItem(SESSION_KEY);
    setStarted(false);
    setSessionId(null);
    setMessages([]);
    setInputType("text");
    setSummary("");
    setStyles([]);
    setStyleLink("");
    setText("");
    setPhase(1);
    setDocId(null);
    setCharacterImage(null);
    setCopied(false);
    setQueueStatus(null);
    setLoadingLabel("");
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1500);
  };

  const downloadDoc = async () => {
    try {
      const res = await fetch(`${API}/doc/${docId}`);
      const blob = await res.blob();
      triggerDownload(blob, "Creative_Provenance_Record.txt");
    } catch (e) {
      /* download blocked */
    }
  };

  const downloadImage = () => {
    if (!characterImage) return;
    const chars = atob(characterImage);
    const bytes = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    triggerDownload(new Blob([bytes], { type: "image/png" }), "my_character.png");
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summary);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = summary;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (err) { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && inputType !== "email") {
      e.preventDefault();
      sendText();
    } else if (e.key === "Enter" && inputType === "email") {
      e.preventDefault();
      sendText();
    }
  };

  // ---------- Resume splash (avoid flashing welcome) ----------
  if (resuming) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <span className="inline-flex items-center gap-2 text-[#C5A059] font-sans text-sm tracking-[0.3em] uppercase">
          <Feather size={16} /> Lonesome Cactus Studio
        </span>
      </div>
    );
  }

  // ---------- Welcome screen ----------
  if (!started) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-16">
        <motion.div {...fade} className="max-w-2xl text-center">
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2 text-[#C5A059] font-sans text-sm tracking-[0.3em] uppercase">
              <Feather size={16} /> Lonesome Cactus Studio
            </span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl text-[#0B2D30] leading-tight">
            The Story in the Drawer
          </h1>
          <p className="mt-6 font-sans text-lg md:text-xl text-[#4A6C6D] leading-relaxed">
            A guided session to discover the visual heart of your story. No need to have it all
            figured out, we'll begin with a single moment and find your way from there.
          </p>
          <button
            onClick={begin}
            disabled={loading}
            data-testid="begin-button"
            className="mt-10 rounded-full bg-[#114A4E] text-[#FAF8F5] px-12 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans disabled:opacity-50 inline-flex items-center gap-2"
          >
            {loading ? "Opening the drawer..." : "Begin your session"}
            {!loading && <Sparkles size={18} />}
          </button>
          <p className="mt-8 font-sans text-sm text-[#4A6C6D]/70">joannelangston.com</p>
        </motion.div>
      </div>
    );
  }

  const progress = Math.min((phase / 9) * 100, 100);
  const isDone = inputType === "done";

  return (
    <div className="min-h-screen">
      {/* Progress */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#FAF8F5]/80 backdrop-blur-md">
        <div className="h-1 bg-[#E8D8B0]">
          <motion.div
            className="h-full bg-[#C5A059]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="font-serif text-base text-[#114A4E]">The Story in the Drawer</span>
          <span className="font-sans text-xs tracking-wider uppercase text-[#4A6C6D]" data-testid="phase-indicator">
            {isDone ? "Complete" : `Phase ${Math.min(phase, 9)} of 9`}
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-28 pb-40">
        <AnimatePresence initial={false}>
          {messages.map((m) =>
            m.role === "guide" ? <GuideMessage key={m.id} msg={m} /> : <ClientMessage key={m.id} msg={m} />
          )}
        </AnimatePresence>

        {loading && <TypingBubble label={loadingLabel} />}

        {!loading && inputType === "summary" && (
          <SummaryReview summary={summary} onConfirm={confirmSummary} onAdjust={adjustSummary} busy={loading} />
        )}

        {!loading && inputType === "style" && (
          <StyleSelect styles={styles} link={styleLink} onSelect={selectStyle} busy={loading} />
        )}

        {!loading && inputType === "generating" && queueStatus && (
          <motion.div
            {...fade}
            data-testid="queue-status"
            className="mb-8 rounded-2xl border border-[#E8D8B0] bg-[#F1EBE1] p-6 md:p-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C5A059] opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C5A059]" />
              </span>
              <span className="font-sans text-xs uppercase tracking-wider text-[#4A6C6D]">
                The studio is painting
              </span>
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#0B2D30]" data-testid="queue-status-text">
              {queueCopy(queueStatus.position, queueStatus.eta)}
            </p>
            <p className="mt-3 font-sans text-sm text-[#4A6C6D] leading-relaxed">
              Your answers are saved. You can keep this page open — your character will appear here the moment it's ready.
            </p>
          </motion.div>
        )}

        {!loading && inputType === "generation_failed" && (
          <motion.div {...fade} className="mb-8" data-testid="generation-failed">
            <button
              onClick={retryGeneration}
              data-testid="retry-generation-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#114A4E] text-[#FAF8F5] px-8 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans"
            >
              <RotateCcw size={18} /> Try again
            </button>
          </motion.div>
        )}

        {!loading && isDone && (
          <motion.div {...fade} className="mb-8" data-testid="final-screen">
            {summary && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl text-[#0B2D30]">Your Story Summary</h3>
                  <button
                    onClick={copySummary}
                    data-testid="copy-summary-button"
                    className="inline-flex items-center gap-1.5 font-sans text-sm text-[#114A4E] hover:text-[#C5A059] transition-colors"
                  >
                    {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy summary</>}
                  </button>
                </div>
                <div
                  data-testid="final-summary-text"
                  className="bg-[#F1EBE1] border border-[#E8D8B0] rounded-2xl p-6 whitespace-pre-wrap font-sans text-base leading-relaxed text-[#0B2D30] select-text"
                >
                  {summary}
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              {docId && (
                <button
                  onClick={downloadDoc}
                  data-testid="download-doc-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border-2 border-[#C5A059] text-[#0B2D30] px-8 py-4 hover:bg-[#C5A059] hover:text-[#FAF8F5] transition-colors duration-300 text-lg font-medium font-sans"
                >
                  <Download size={18} /> Download your Creative Provenance Record
                </button>
              )}
              <button
                onClick={startOver}
                data-testid="start-over-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#114A4E] text-[#FAF8F5] px-8 py-4 hover:bg-[#0A3336] transition-colors duration-300 text-lg font-medium font-sans"
              >
                <RotateCcw size={18} /> Begin a new story
              </button>
            </div>
            {characterImage && (
              <button
                onClick={downloadImage}
                data-testid="download-image-button"
                className="mt-4 inline-flex items-center gap-2 font-sans text-sm text-[#114A4E] underline underline-offset-4 hover:text-[#C5A059] transition-colors"
              >
                <Download size={15} /> Save your character image
              </button>
            )}
            <p className="mt-8 font-sans text-sm text-[#4A6C6D]/70">
              Lonesome Cactus Studio · joannelangston.com
            </p>
          </motion.div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input bar */}
      {!loading && (inputType === "text" || inputType === "image_feedback" || inputType === "email") && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-[#FAF8F5] via-[#FAF8F5] to-transparent pt-8"
        >
          <div className="max-w-3xl mx-auto px-6 pb-6">
            <div className="flex items-end gap-3">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                autoFocus
                placeholder={
                  inputType === "email" ? "your@email.com" : "Type your response..."
                }
                data-testid="story-input-field"
                className="flex-1 bg-transparent border-0 border-b-2 border-[#E8D8B0] focus:border-[#C5A059] text-[#0B2D30] text-xl md:text-2xl font-serif resize-none py-3 placeholder-[#4A6C6D]/40 transition-colors"
              />
              <AnimatePresence>
                {text.trim() && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={sendText}
                    data-testid="next-phase-button"
                    className="shrink-0 rounded-full bg-[#114A4E] text-[#FAF8F5] p-4 hover:bg-[#0A3336] transition-colors duration-300"
                  >
                    <ArrowRight size={22} />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
